<ng-container *transloco="let t">
  @if (isOpen()) {
    @if (isBottomSheet()) {
      <div
        id="mfa-prefs-bs-backdrop"
        class="fixed inset-0 z-[70] flex items-end bg-black/50 keyboard-aware-container"
        [style.padding-bottom.px]="keyboardHeight()"
      >
        <div
          id="mfa-prefs-bs-container"
          class="bg-white w-full rounded-t-2xl shadow-xl flex flex-col overflow-hidden mfa-prefs-bottom-sheet-slide-up"
          [style.max-height]="'calc(100dvh - env(safe-area-inset-top, 0px) - ' + keyboardHeight() + 'px)'"
          style="padding-bottom: env(safe-area-inset-bottom, 0px)"
        >
          <div
            id="mfa-prefs-bs-header"
            class="flex items-center justify-between px-4 py-3 bg-black rounded-t-2xl shrink-0"
          >
            <div class="flex items-center gap-2">
              <span class="font-dm text-base font-semibold text-white">{{ modalTitle() }}</span>
            </div>
            <button
              id="mfa-prefs-bs-close-btn"
              type="button"
              class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
              aria-label="Close"
              (click)="onClose()"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div id="mfa-prefs-bs-content" class="p-6 overflow-y-auto flex-1">
            @if (mfaLoading()) {
              <div id="mfa-prefs-bs-loading" class="flex flex-col items-center justify-center py-8">
                <mat-spinner diameter="48"></mat-spinner>
                <p class="mt-4 text-ld-neutral-500 font-dm text-sm">{{ t('commonMessagesLoading') }}...</p>
              </div>
            } @else {
              @switch (currentStep()) {
                @case ('verify') {
                  <div class="p-0">
                    @if (mfaError()) {
                      <div
                        id="mfa-prefs-bs-verify-error"
                        role="alert"
                        class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                      >
                        {{ mfaError() }}
                      </div>
                    }
                    <p class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-6 text-left">
                      {{ t('mfaPreferencesMfaChallengeDescription') }}
                    </p>
                    <div class="flex flex-col gap-3">
                      <button
                        id="mfa-prefs-bs-continue-btn"
                        type="button"
                        class="btn-primary-ld-purple w-full order-1"
                        [disabled]="verifyLoading() || !methodsLoaded()"
                        [attr.aria-busy]="verifyLoading()"
                        (click)="onContinueVerify()"
                      >
                        @if (verifyLoading()) {
                          <span class="inline-flex items-center justify-center">
                            <span
                              class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                            ></span>
                          </span>
                        } @else {
                          {{ t('commonButtonsContinue') | uppercase }}
                        }
                      </button>
                      <button
                        id="mfa-prefs-bs-cancel-btn"
                        type="button"
                        class="btn-outline-ld-purple order-2"
                        (click)="onClose()"
                      >
                        {{ t('commonButtonsCancel') }}
                      </button>
                    </div>
                  </div>
                }

                @case ('form') {
                  <div class="p-0">
                    @if (mfaError()) {
                      <div
                        id="mfa-prefs-bs-error"
                        role="alert"
                        class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                      >
                        {{ mfaError() }}
                      </div>
                    }
                    @if (noMethodsAvailable()) {
                      <p id="mfa-prefs-bs-no-methods" class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-6">
                        {{ t('mfaPreferencesNoMethodsMessage') }}
                        <a
                          id="mfa-prefs-bs-support-link"
                          [href]="supportPhoneNumberTel"
                          class="font-semibold text-ld-primary underline"
                        >
                          {{ supportPhoneNumber }}
                        </a>
                      </p>

                      <div class="flex justify-center mt-6">
                        <button
                          id="mfa-prefs-bs-no-methods-close-btn"
                          type="button"
                          class="btn-primary-ld-purple min-w-[220px]"
                          (click)="onClose()"
                        >
                          {{ t('commonButtonsClose') }}
                        </button>
                      </div>
                    } @else {
                      <p id="mfa-prefs-bs-description" class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-6">
                        {{ t(descriptionKey()) }}
                      </p>

                      <form [formGroup]="form" class="flex flex-col gap-5">
                        @if (showPhoneField()) {
                          <div>
                            <mat-form-field appearance="outline" class="w-full">
                              <mat-label>{{ t('mfaPreferencesPhoneNumber') }}</mat-label>
                              <mat-select formControlName="phoneMethodId">
                                @for (phone of phoneFormOptions(); track phone.value) {
                                  <mat-option [value]="phone.value">
                                    {{ phone.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            @if (phoneError()) {
                              <p class="text-xs font-dm text-ld-error mt-1 mb-0">
                                {{ t(phoneError()!) }}
                              </p>
                            }
                          </div>
                        }

                        @if (showEmailField()) {
                          <div>
                            <mat-form-field appearance="outline" class="w-full">
                              <mat-label>{{ t('mfaPreferencesEmailAddress') }}</mat-label>
                              <mat-select formControlName="emailMethodId">
                                @for (email of emailFormOptions(); track email.value) {
                                  <mat-option [value]="email.value">
                                    {{ email.label }}
                                  </mat-option>
                                }
                              </mat-select>
                            </mat-form-field>
                            @if (emailError()) {
                              <p class="text-xs font-dm text-ld-error mt-1 mb-0">
                                {{ t(emailError()!) }}
                              </p>
                            }
                          </div>
                        }
                      </form>

                      <div class="flex justify-center mt-6">
                        <button
                          id="mfa-prefs-bs-update-btn"
                          type="button"
                          class="btn-primary-ld-purple min-w-[220px]"
                          [disabled]="isLoading() || !methodsLoaded() || !identityVerified()"
                          (click)="onUpdatePreferences()"
                        >
                          @if (isLoading()) {
                            <span class="inline-flex items-center gap-2">
                              <span
                                class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                              ></span>
                            </span>
                          } @else {
                            {{ t('mfaPreferencesUpdateButton') }}
                          }
                        </button>
                      </div>
                    }
                  </div>
                }

                @case ('success') {
                  <div class="p-0">
                    <div class="flex items-center gap-2 mb-3">
                      <ld-svg-icon [src]="checkBadgeIcon" class="size-6 shrink-0 text-ld-primary" aria-hidden="true" />
                      <h2 id="mfa-prefs-bs-success-title" class="font-dm text-lg font-semibold text-ld-neutral-700 m-0">
                        {{ t('mfaPreferencesSuccessTitle') }}
                      </h2>
                    </div>
                    <p
                      id="mfa-prefs-bs-success-message"
                      class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-6"
                    >
                      {{ t('mfaPreferencesSuccessMessage') }}
                    </p>
                    <div class="flex justify-center">
                      <button
                        id="mfa-prefs-bs-close-success-btn"
                        type="button"
                        class="btn-primary-ld-purple min-w-[100px]"
                        (click)="onClose()"
                      >
                        {{ t('commonButtonsClose') }}
                      </button>
                    </div>
                  </div>
                }
              }
            }
          </div>
        </div>
      </div>
    } @else {
      <ld-base-modal
        [title]="modalTitle()"
        [showCloseButton]="true"
        [isLoading]="isLoading() || mfaLoading()"
        [closeOnBackdropClick]="!isLoading() && !mfaLoading()"
        size="fit"
        (closeModal)="onClose()"
      >
        @if (currentStep() === 'success') {
          <ld-svg-icon
            modal-header-prefix
            [src]="checkBadgeIcon"
            class="size-6 shrink-0 text-ld-primary"
            aria-hidden="true"
          />
        }

        <div modal-content>
          @switch (currentStep()) {
            @case ('verify') {
              <div class="p-0">
                @if (mfaError()) {
                  <div
                    role="alert"
                    class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                  >
                    {{ mfaError() }}
                  </div>
                }
                <p class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-0 text-left">
                  {{ t('mfaPreferencesMfaChallengeDescription') }}
                </p>
              </div>
            }

            @case ('form') {
              <div class="p-0">
                @if (mfaError()) {
                  <div
                    role="alert"
                    class="mb-4 rounded-lg border border-ld-error/30 bg-ld-error/10 px-4 py-3 font-dm text-sm text-ld-error"
                  >
                    {{ mfaError() }}
                  </div>
                }
                @if (noMethodsAvailable()) {
                  <p id="mfa-prefs-no-methods" class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-0">
                    {{ t('mfaPreferencesNoMethodsMessage') }}
                    <a
                      id="mfa-prefs-support-link"
                      [href]="supportPhoneNumberTel"
                      class="font-semibold text-ld-primary underline"
                    >
                      {{ supportPhoneNumber }}
                    </a>
                  </p>
                } @else {
                  <p id="mfa-prefs-description" class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-6">
                    {{ t(descriptionKey()) }}
                  </p>

                  <form [formGroup]="form" class="flex flex-col gap-5">
                    @if (showPhoneField()) {
                      <div>
                        <mat-form-field appearance="outline" class="w-full">
                          <mat-label>{{ t('mfaPreferencesPhoneNumber') }}</mat-label>
                          <mat-select formControlName="phoneMethodId">
                            @for (phone of phoneFormOptions(); track phone.value) {
                              <mat-option [value]="phone.value">
                                {{ phone.label }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                        @if (phoneError()) {
                          <p class="text-xs font-dm text-ld-error mt-1 mb-0">
                            {{ t(phoneError()!) }}
                          </p>
                        }
                      </div>
                    }

                    @if (showEmailField()) {
                      <div>
                        <mat-form-field appearance="outline" class="w-full">
                          <mat-label>{{ t('mfaPreferencesEmailAddress') }}</mat-label>
                          <mat-select formControlName="emailMethodId">
                            @for (email of emailFormOptions(); track email.value) {
                              <mat-option [value]="email.value">
                                {{ email.label }}
                              </mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                        @if (emailError()) {
                          <p class="text-xs font-dm text-ld-error mt-1 mb-0">
                            {{ t(emailError()!) }}
                          </p>
                        }
                      </div>
                    }
                  </form>
                }
              </div>
            }

            @case ('success') {
              <div class="p-0">
                <p class="text-sm font-dm text-ld-neutral-500 leading-5 mt-0 mb-0">
                  {{ t('mfaPreferencesSuccessMessage') }}
                </p>
              </div>
            }
          }

          @if (mfaLoading()) {
            <div class="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
              <mat-spinner diameter="48"></mat-spinner>
            </div>
          }
        </div>

        <div modal-footer>
          @switch (currentStep()) {
            @case ('verify') {
              <button
                id="mfa-prefs-continue-btn"
                type="button"
                class="btn-primary-ld-purple min-w-[220px]"
                [disabled]="verifyLoading() || !methodsLoaded()"
                [attr.aria-busy]="verifyLoading()"
                (click)="onContinueVerify()"
              >
                @if (verifyLoading()) {
                  <span class="inline-flex items-center justify-center">
                    <span
                      class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                    ></span>
                  </span>
                } @else {
                  {{ t('commonButtonsContinue') | uppercase }}
                }
              </button>
            }

            @case ('form') {
              @if (noMethodsAvailable()) {
                <button type="button" class="btn-primary-ld-purple min-w-[100px]" (click)="onClose()">
                  {{ t('commonButtonsClose') }}
                </button>
              } @else {
                <button
                  type="button"
                  class="btn-primary-ld-purple min-w-[220px]"
                  [disabled]="isLoading() || !methodsLoaded() || !identityVerified()"
                  (click)="onUpdatePreferences()"
                >
                  @if (isLoading()) {
                    <span class="inline-flex items-center gap-2">
                      <span
                        class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      ></span>
                    </span>
                  } @else {
                    {{ t('mfaPreferencesUpdateButton') }}
                  }
                </button>
              }
            }

            @case ('success') {
              <button type="button" class="btn-primary-ld-purple min-w-[100px]" (click)="onClose()">
                {{ t('commonButtonsClose') }}
              </button>
            }
          }
        </div>
      </ld-base-modal>
    }
  }
</ng-container>
